import { heroTags, quickStats, videos } from "@/mocks/homeData";

interface HeroProps {
  query: string;
  onQueryChange: (value: string) => void;
  onPickTag: (tag: string) => void;
}

const showcase = [videos[0], videos[4], videos[3]];

export default function Hero({ query, onQueryChange, onPickTag }: HeroProps) {
  const goExplore = () => {
    document.getElementById("explore")?.scrollIntoView({ behavior: "smooth" });
  };

  const handleTag = (tag: string) => {
    onPickTag(tag);
    goExplore();
  };

  return (
    <section id="top" className="relative w-full overflow-hidden pt-24 md:pt-28">
      <div className="pointer-events-none absolute -right-24 top-10 hidden h-72 w-72 rounded-full bg-accent-200/50 blur-3xl lg:block"></div>
      <div className="pointer-events-none absolute -left-20 top-40 hidden h-64 w-64 rounded-full bg-primary-100/70 blur-3xl lg:block"></div>

      <div className="relative mx-auto grid w-full max-w-6xl grid-cols-1 gap-10 px-4 pb-12 md:px-6 lg:grid-cols-[1.1fr_0.9fr] lg:items-center lg:pb-16">
        <div className="animate-fade-up">
          <span className="inline-flex items-center gap-2 rounded-full border border-primary-200 bg-primary-50 px-3 py-1 font-label text-xs font-semibold text-primary-700">
            <span className="h-1.5 w-1.5 animate-pulse-soft rounded-full bg-primary-500"></span>
            每日精选热门排行 · 内容持续更新
          </span>

          <h1 className="mt-5 font-heading text-3xl font-extrabold leading-tight text-foreground-950 md:text-5xl">
            小视频热门合集
            <span className="block text-primary-600">一页刷到值得看的</span>
          </h1>

          <p className="mt-5 max-w-xl text-sm leading-relaxed text-foreground-600 md:text-base">
            本页聚合当下热门的小视频合集与精选排行，涵盖美食、旅行、萌宠、手工、数码等主题，
            帮你快速定位值得观看的精彩视频，满足日常刷片、找热门、追推荐的使用需求，省去来回翻找的麻烦。
          </p>

          <div className="mt-7 flex flex-col gap-3 sm:flex-row">
            <div className="flex w-full items-center gap-2 rounded-full border border-background-300 bg-background-50 px-4 py-2 sm:max-w-md">
              <i className="ri-search-line text-lg text-foreground-500"></i>
              <input
                type="text"
                name="site_search"
                value={query}
                onChange={(e) => onQueryChange(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") goExplore();
                }}
                placeholder="输入关键词，如：美食、旅行、萌宠"
                className="h-9 w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
              />
            </div>
            <button
              type="button"
              onClick={goExplore}
              className="flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-play-list-2-line text-base"></i>
              浏览热门内容
            </button>
          </div>

          <div className="mt-6 flex flex-wrap items-center gap-2">
            <span className="font-label text-xs font-medium text-foreground-500">今日热词</span>
            {heroTags.map((tag) => (
              <button
                key={tag}
                type="button"
                onClick={() => handleTag(tag)}
                className="cursor-pointer whitespace-nowrap rounded-full bg-secondary-100 px-3 py-1 text-xs font-medium text-secondary-900 transition-colors hover:bg-secondary-200"
              >
                #{tag}
              </button>
            ))}
          </div>

          <dl className="mt-8 grid grid-cols-2 gap-4 border-t border-background-200 pt-6 sm:grid-cols-4">
            {quickStats.map((stat) => (
              <div key={stat.label}>
                <dt className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">{stat.value}</dt>
                <dd className="mt-1 text-xs text-foreground-500">{stat.label}</dd>
              </div>
            ))}
          </dl>
        </div>

        <div className="relative hidden lg:block">
          <div className="grid grid-cols-3 gap-4">
            {showcase.map((item, index) => (
              <figure
                key={item.id}
                className={`overflow-hidden rounded-lg border border-background-200 bg-background-100 ${
                  index === 1 ? "mt-8 animate-float-y" : ""
                }`}
              >
                <div className="relative h-64 w-full">
                  <img
                    src={item.cover}
                    alt={`${item.title} · 小视频热门合集封面`}
                    title={`${item.title} · 热门小视频`}
                    className="h-full w-full object-cover object-top"
                  />
                  <span className="absolute left-2 top-2 rounded-md bg-foreground-950/70 px-2 py-0.5 font-label text-[10px] font-semibold text-background-50">
                    {item.category}
                  </span>
                  <span className="absolute bottom-2 right-2 rounded-md bg-foreground-950/70 px-2 py-0.5 font-label text-[10px] font-semibold text-background-50">
                    {item.duration}
                  </span>
                </div>
                <figcaption className="px-3 py-3">
                  <p className="line-clamp-2 text-xs font-semibold text-foreground-900">{item.title}</p>
                  <p className="mt-1 flex items-center gap-1 text-[11px] text-foreground-500">
                    <i className="ri-eye-line"></i>
                    {item.views}
                  </p>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}